import { showSuccess, showError } from '@calendly/alert';
import { Form } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { useEditorContent } from '@calendly/rich-text-editor';
import { BareButton } from '@calendly/ui/components/bare-button';
import { IconX } from '@calendly/ui/components/glyphs/IconX';
import { SimplePopup } from '@calendly/ui/components/popup';
import { Sheet } from '@calendly/ui/components/sheet';
import { HeadlineText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, useEffect, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { useGetUserQuery, useGetOrganizationUuid } from 'common';
import LoadingState from 'components/core/loading_state';
import Settings from 'core/settings';
import { useNotetakerEntitled } from 'features/meeting_intelligence';
import { useGetMeetingQuery, useGetInviteesQuery } from 'features/meetings';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events';
import { useSendMeetingShareEmailMutation, useFetchAttendeeFollowUpEmailQuery } from 'hooks/meeting_shares/api';
import { useFetchTrackedMeetingWithSummaryQuery } from 'hooks/tracked_meetings/api';
import ShareRecap from 'images/tracked_meetings/share-recap.svg';
import { isCalendlyForeignEventProvider } from 'shared/utils';
import { validationSchema } from './config';
import { Footer } from './footer';
import { SendFromCalendlyEmailForm } from './form';
import { createEmailBody, createAttendeeEmailBody, parseInvitees } from './helpers';
import { TARGETS } from '../../constants';
const noop = () => {};
const normalizeTrackedMeetingSummaries = summaries => {
  if (!Array.isArray(summaries?.summaries)) return summaries;
  const getContent = target => summaries.summaries.find(summary => summary.name === target)?.value ?? [];
  return {
    summary: getContent(TARGETS.SUMMARY),
    actionItems: getContent(TARGETS.ACTION_ITEMS),
    topics: getContent(TARGETS.DISCUSSION)
  };
};
const hasSummaryContent = summaries => summaries?.summary?.length > 0 || summaries?.actionItems?.length > 0 || summaries?.topics?.length > 0;
const shareContainerCss = "sydnjdg";
const ShareContainer = forwardRef((props, ref) => <Sheet ref={ref} {...props} className={classNames(shareContainerCss, props.className)} />);
const shareModalHeaderIsPhoneCss = "sql1mmg";
const shareModalHeaderCss = "sipgtv5";
const ShareModalHeader = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(shareModalHeaderCss, {
    [shareModalHeaderIsPhoneCss]: isPhone
  }, props.className)} />;
});
const alignmentContainerCss = "ajebvkv";
const AlignmentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(alignmentContainerCss, props.className)} />;
});
const modalContentWrapperCss = "mci6np3";
const ModalContentWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(modalContentWrapperCss, props.className)} />;
});
const styledBareButtonCss = "s1y444y7";
const StyledBareButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(styledBareButtonCss, props.className)} />);
const styledPopupCss = "s1qxujyy";
const StyledPopup = forwardRef((props, ref) => <SimplePopup ref={ref} {...props} className={classNames(styledPopupCss, props.className)} />);
export const EmailComposer = ({
  onClose,
  onBack,
  emails,
  onShareLinkClick,
  videoPageUrl,
  isConfirmationPopupOpen,
  handleOpenConfirmationPopup,
  handleCloseConfirmationPopup,
  handleConfirmAction,
  closeModal,
  isAttendeeModal,
  attendeesFromSearchData,
  meetingUuid,
  shareUuid,
  accessLevel,
  videoPasscode,
  templateSummaries,
  includePasscode = true,
  includePublicLink = true,
  onBeforeSubmit = noop
}) => {
  const {
    isEntitled: isNotetakerEntitled
  } = useNotetakerEntitled();
  const [isEditorReady, {
    fromText: textToEditorContent,
    fromHTML: htmlToEditorContent
  }] = useEditorContent();
  const {
    data: trackedMeeting,
    isLoading: trackedMeetingLoading
  } = useFetchTrackedMeetingWithSummaryQuery({
    meetingUuid
  });
  const isCalendlyMeeting = isCalendlyForeignEventProvider(trackedMeeting?.foreignEventProvider);
  // Prefer normalized template summaries when available; the fetched meeting summaries remain a
  // fallback for share flows rendered outside TemplateSummaryProvider.
  const trackedMeetingSummaries = normalizeTrackedMeetingSummaries(trackedMeeting?.summaries);
  const summaries = isNotetakerEntitled && hasSummaryContent(templateSummaries) ? templateSummaries : trackedMeetingSummaries;
  const trackedMeetingForeignEventId = trackedMeeting?.foreignEventId;
  const {
    data: event
  } = useGetMeetingQuery({
    eventId: trackedMeetingForeignEventId,
    selectedView: 'scheduled'
  }, {
    skip: !isCalendlyMeeting
  });
  const [invitees, setInvitees] = useState(null);
  const {
    data: inviteesData,
    isSuccess: inviteesSuccess
  } = useGetInviteesQuery({
    eventId: event?.id,
    page: 1
  }, {
    skip: !event || !isCalendlyMeeting
  });
  useEffect(() => {
    if (isCalendlyMeeting) {
      if (inviteesSuccess) setInvitees(inviteesData);
    } else {
      setInvitees(trackedMeeting?.participants.filter(participant => participant.role !== 'host'));
    }
  }, [inviteesSuccess, inviteesData, isCalendlyMeeting, trackedMeeting?.participants]);
  const [sendMeetingShareEmail] = useSendMeetingShareEmailMutation();
  const {
    data: user
  } = useGetUserQuery();
  const organizationUuid = useGetOrganizationUuid();
  const dispatch = useDispatch();
  const [shouldFetchFollowupSummary, setShouldFetchFollowupSummary] = useState(false);
  useEffect(() => {
    if (trackedMeeting && invitees) {
      setShouldFetchFollowupSummary(true);
    }
  }, [trackedMeeting, invitees]);

  // Fields to pass to api for follow up email summary
  const summary = summaries?.summary?.[0]?.text;
  const hosts = [user.profile.name];
  const inviteeNamesForFollowup = isCalendlyMeeting ? invitees?.results.map(invitee => invitee.name) : [];
  const {
    data: followupEmail
  } = useFetchAttendeeFollowUpEmailQuery({
    summary,
    event_uuid: meetingUuid,
    hosts,
    invitees: inviteeNamesForFollowup
  }, {
    skip: !shouldFetchFollowupSummary || !isAttendeeModal
  });
  const onSubmitEmail = async formState => {
    const emailStrings = formState.to_addresses.map(({
      email
    }) => email);
    const trackedMeetingOwnerId = user.id;
    const trackedMeetingOwnerUuid = user.uuid;
    // payload for Segment tracking pixel is base64 encoded JSON
    const jsonSegmentPayload = JSON.stringify({
      writeKey: Settings.segment.client_write_key,
      event: TRACKING_EVENT_NAMES.AI_NOTETAKER_ENGAGED,
      userId: String(trackedMeetingOwnerId),
      version: 1,
      properties: {
        step: 'recipient_viewed_summary_email',
        user_id: trackedMeetingOwnerId,
        user_uuid: trackedMeetingOwnerUuid,
        organization_id: user?.organizationMembership?.id,
        organization_uuid: organizationUuid,
        is_calendly_meeting: isCalendlyMeeting,
        event_uuid: trackedMeetingForeignEventId,
        share_uuid: shareUuid,
        share_method: 'email',
        mi_event_uuid: meetingUuid
      }
    });
    const encodedSegmentPayload = btoa(jsonSegmentPayload);
    const values = {
      send_with: formState.send_with,
      to_addresses: emailStrings,
      subject: formState.subject.plainText,
      body: formState.body.html,
      video_url: includePublicLink && formState.include_public_link ? videoPageUrl : null,
      segment_payload: encodedSegmentPayload,
      ...(includePasscode ? {
        video_passcode: !includePublicLink || !formState.include_public_link || formState.exclude_passcode ? null : videoPasscode
      } : {})
    };
    try {
      await onBeforeSubmit?.(formState);
      if (formState.send_with.type === 'calendly') {
        // The Calendly internal mailer only processes the first address in to_addresses,
        // so send one request per recipient. This also enables per-recipient features
        // like unsubscribe links in the future.
        await Promise.all(emailStrings.map(address => sendMeetingShareEmail({
          ...values,
          to_addresses: [address]
        })));
      } else {
        await sendMeetingShareEmail(values);
      }
      onShareLinkClick('email');
      dispatch(showSuccess(I18n.t('publisher.meeting_recap.share_modal.email.success_toast')));
      closeModal(onClose);
    } catch (error) {
      dispatch(showError(I18n.t('publisher.meeting_recap.share_modal.email.error_toast')));
    }
  };
  const isLoaded = !isAttendeeModal || invitees && followupEmail;
  if (!isEditorReady || !isLoaded || trackedMeetingLoading) {
    return <ShareContainer>
        <ModalContentWrapper>
          <LoadingState />
        </ModalContentWrapper>
      </ShareContainer>;
  }
  const [inviteeNames, inviteeEmails] = parseInvitees(event, isCalendlyMeeting && invitees?.results?.length > 0 ? invitees.results.filter(invitee => invitee.name !== user.name) : attendeesFromSearchData?.filter(attendee => attendee.email !== user.email) || []);
  const initialBody = isAttendeeModal && followupEmail ? createAttendeeEmailBody(trackedMeeting?.name, user.name, inviteeNames, summaries, followupEmail) : createEmailBody(trackedMeeting?.name, user.name, inviteeNames, summaries);
  const initialSubject = I18n.t('publisher.meeting_intelligence.share.default_email_subject', {
    event: trackedMeeting?.name
  });
  const selectedEmail = emails.length > 1 ? emails[1] : emails[0];
  const formInitialValues = {
    send_with: {
      type: selectedEmail.type,
      email: selectedEmail.emailAddress
    },
    to_addresses: isAttendeeModal ? inviteeEmails : [],
    subject: textToEditorContent(initialSubject),
    body: htmlToEditorContent(initialBody),
    include_public_link: includePublicLink,
    enable_passcode: includePasscode && accessLevel === 'Passcode',
    exclude_passcode: true
  };
  return <>
      <ShareContainer>
        <ShareModalHeader>
          <HeadlineText level={2}>
            <AlignmentContainer>
              <img src={ShareRecap} alt="" />
              {I18n.t('publisher.meeting_intelligence.share.modal_header_la')}
            </AlignmentContainer>
          </HeadlineText>
          <StyledBareButton onClick={() => handleOpenConfirmationPopup('close')} aria-label={I18n.t('publisher.action.close')}>
            <UiIcon icon={IconX} size="medium" title="" />
          </StyledBareButton>
        </ShareModalHeader>
        <ModalContentWrapper>
          <Form initialValues={formInitialValues} validationSchema={validationSchema()} onSubmit={onSubmitEmail}>
            <SendFromCalendlyEmailForm initialBody={initialBody} emails={emails} isLoading={!isLoaded} includePasscode={includePasscode} includePublicLink={includePublicLink} />
            <Footer onBack={() => handleOpenConfirmationPopup('back')} isLoading={!isLoaded} />
          </Form>
        </ModalContentWrapper>
      </ShareContainer>
      {isConfirmationPopupOpen && isNotetakerEntitled && <StyledPopup isDestructive title={I18n.t('publisher.meeting_intelligence.share.email_modal_close_header')} onConfirm={() => handleConfirmAction(onClose, onBack)} onClose={handleCloseConfirmationPopup} cancelLabel={I18n.t('publisher.meeting_intelligence.share.email_modal_close_cancel')} confirmLabel={I18n.t('publisher.meeting_intelligence.share.email_modal_close_confirm')}>
          {I18n.t('publisher.meeting_intelligence.share.email_modal_close_warning')}
        </StyledPopup>}
      {isConfirmationPopupOpen && !isNotetakerEntitled && <SimplePopup title={I18n.t('publisher.contacts.confirmation_popup.header')} onConfirm={() => handleConfirmAction(onClose, onBack)} onClose={handleCloseConfirmationPopup} cancelLabel={I18n.t('publisher.contacts.confirmation_popup.cancel')} confirmLabel={I18n.t('publisher.contacts.confirmation_popup.confirm')} size="medium">
          {I18n.t('publisher.meeting_intelligence.share.email_modal_close_warning')}
        </SimplePopup>}
    </>;
};

require("./email_composer.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./email_composer.js");